import { useState, useCallback } from 'react';

/**
 * Custom hook for managing cell editing in tables
 * Handles inline editing of custom field values
 * 
 * @param {Function} onSave - Callback when cell edit is saved
 * @returns {Object} - Editing state and operations
 */
const useCellEditing = (onSave) => {
  const [editingCell, setEditingCell] = useState(null); // { entityId, fieldId, entityType }
  const [editingCellValue, setEditingCellValue] = useState('');

  /**
   * Start editing a cell
   * 
   * @param {string} entityId - Entity ID (company or contact)
   * @param {string} fieldId - Field ID
   * @param {string} entityType - Entity type ('companies' or 'contacts')
   * @param {string} currentValue - Current cell value
   */
  const startEdit = useCallback((entityId, fieldId, entityType, currentValue) => {
    setEditingCell({ entityId, fieldId, entityType });
    setEditingCellValue(currentValue || '');
  }, []);

  /**
   * Cancel editing without saving
   */
  const cancelEdit = useCallback(() => {
    setEditingCell(null);
    setEditingCellValue('');
  }, []);

  /**
   * Save cell edit
   * 
   * @param {string} entityId - Entity ID
   * @param {string} fieldId - Field ID
   * @param {string} newValue - New cell value
   * @param {string} entityType - Entity type ('companies' or 'contacts')
   */
  const saveEdit = useCallback(async (entityId, fieldId, newValue, entityType) => {
    if (onSave) {
      await onSave(entityId, fieldId, newValue, entityType);
    }
    setEditingCell(null);
    setEditingCellValue('');
  }, [onSave]);

  return {
    editingCell,
    editingCellValue,
    setEditingCell,
    setEditingCellValue,
    startEdit,
    cancelEdit,
    saveEdit,
  };
};

export default useCellEditing;

